import React, { useRef, useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
} from "@mui/material";
import { Download, ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import { useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { FeeReceipt } from "../../services/paymentService";

interface FeeReceiptTemplateProps {
    receipt: FeeReceipt | null;
    onBack?: () => void;
}

const FeeReceiptTemplate: React.FC<FeeReceiptTemplateProps> = ({ receipt, onBack }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const schoolId = user?.schoolId;
    const { useSchoolById, useSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
    const { data: schoolData, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
    const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolId ?? 1);

    const [isDownloading, setIsDownloading] = useState(false);
    const templateRef = useRef<HTMLDivElement>(null);

    const schoolName = schoolData?.nameofSchool || "GOVT. SCHOOL";

    const numberToWords = (num: number): string => {
        const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const format = (n: number): string => {
            if (n < 20) return a[n];
            if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
            if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + format(n % 100) : '');
            if (n < 100000) {
                const thousands = Math.floor(n / 1000);
                return format(thousands) + ' Thousand' + (n % 1000 !== 0 ? ' ' + format(n % 1000) : '');
            }
            if (n < 10000000) {
                const lakhs = Math.floor(n / 100000);
                return format(lakhs) + ' Lakh' + (n % 100000 !== 0 ? ' ' + format(n % 100000) : '');
            }
            const crores = Math.floor(n / 10000000);
            return format(crores) + ' Crore' + (n % 10000000 !== 0 ? ' ' + format(n % 10000000) : '');
        };

        if (num === 0) return 'Zero';
        const result = format(Math.floor(num));
        return result.trim() + ' Only';
    };

    const formatDateTime = (date: any) => {
        if (!date) return "N/A";
        if (Array.isArray(date)) {
            const [year, month, day, hour, minute] = date;
            const ampm = (hour || 0) >= 12 ? 'PM' : 'AM';
            const displayHour = (hour || 0) % 12 || 12;
            const displayMinute = String(minute || 0).padStart(2, '0');
            return `${day}/${month}/${year} ${displayHour}:${displayMinute} ${ampm}`;
        }
        const d = new Date(date);
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        const displayHour = d.getHours() % 12 || 12;
        const displayMinute = String(d.getMinutes()).padStart(2, '0');
        return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${displayHour}:${displayMinute} ${ampm}`;
    };

    const downloadAsImage = async () => {
        if (!templateRef.current) return;
        setIsDownloading(true);
        try {
            const canvas = await html2canvas(templateRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
            } as any);
            const link = document.createElement("a");
            link.download = `receipt-${receipt?.paymentId || "print"}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
        } catch (error) {
            console.error("Failed to download receipt:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    if (schoolLoading || !receipt) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    const student = receipt.feeTransaction?.studentAccount?.student;
    const studentName = receipt.studentName || student?.studentName || "N/A";
    const scholarNo = student?.scholarNo || "N/A";
    const fatherName = student?.fatherName || "-";

    // Use session and school class from the feeTransaction object as requested
    const transactionSession = receipt.feeTransaction?.session;
    const transactionClass = receipt.feeTransaction?.schoolClass;

    const sessionName = transactionSession?.name || student?.sessionName || student?.session?.name || receipt.sessionId || "N/A";
    const classBase = transactionClass?.name || student?.className || student?.schoolClass?.name || receipt.classId || "N/A";
    const section = transactionClass?.section || student?.section || student?.schoolClass?.section || "";
    const className = section ? `${classBase} ${section}` : classBase;

    const totalAmount = receipt.amount || receipt.feeTransaction?.amount || 0;
    const dueAmount = receipt.feeTransaction?.studentAccount?.dueAmount;
    const dateTime = formatDateTime(receipt.feeTransaction?.createdAt || receipt.localDateTime);
    const payMode = receipt.feeTransaction?.paymentType || "CASH";
    const receiptNo = receipt.id || receipt.paymentId || "N/A";

    const tableStyle: React.CSSProperties = {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: "10px",
        fontSize: "0.9rem",
    };

    const thStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "8px",
        backgroundColor: "#e0e0e0",
        textAlign: "left",
        fontWeight: "bold",
    };

    const tdStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "8px",
        textAlign: "left",
    };

    const headerCellStyle: React.CSSProperties = {
        padding: "4px 8px",
        fontSize: "0.95rem",
    };

    return (
        <Box sx={{ p: 2, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }} className="no-print">
                {onBack && (
                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>
                        {t("back")}
                    </Button>
                )}
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button variant="contained" startIcon={<Download />} onClick={downloadAsImage} disabled={isDownloading}>
                        {isDownloading ? t("downloading") : t("download")}
                    </Button>
                </Box>
            </Box>

            <Box
                ref={templateRef}
                sx={{
                    bgcolor: "#fff",
                    p: 4,
                    width: "100%",
                    maxWidth: "800px",
                    margin: "0 auto",
                    border: "2px solid #000",
                    fontFamily: "'Times New Roman', Times, serif",
                    color: "#000",
                    boxSizing: "border-box",
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2, gap: 2, px: 3, py: 1 }}>
                    {/* School Logo */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            border: "3px solid #fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            overflow: "hidden",
                            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                        }}
                    >
                        {logoLoading ? (
                            <CircularProgress
                                size={24}
                                sx={{ color: "white" }}
                            />
                        ) : logoInfo && logoInfo.fileName ? (
                            <img
                                src={`${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`}
                                alt={`${schoolName} Logo`}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                }}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `
                                            <div style="
                                                color: white; 
                                                font-weight: bold; 
                                                font-size: 14px; 
                                                text-align: center; 
                                                line-height: 1.2;
                                                display: flex;
                                                align-items: center;
                                                justify-content: center;
                                                width: 100%;
                                                height: 100%;
                                            ">
                                                ${schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    textAlign: "center",
                                    lineHeight: 1.2,
                                }}
                            >
                                {schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ textTransform: "uppercase", mb: 1, fontSize: "30px", color: "#000" }}>
                            {schoolName}
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: "18px", color: "#666" }}>
                            {schoolData?.blockName || ""}, {schoolData?.district || ""}, {schoolData?.state || ""} {schoolData?.pinNo || ""}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ border: "1px solid #000", bgcolor: "#e0e0e0", textAlign: "center", py: 0.5, mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                        FEE RECEIPT
                    </Typography>
                </Box>

                {/* Receipt Info Grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #000", pb: 1, mb: 1 }}>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Receipt No</Typography>
                        <Typography sx={headerCellStyle}>: {receiptNo}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Date & Time</Typography>
                        <Typography sx={headerCellStyle}>: {dateTime}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Scholar No</Typography>
                        <Typography sx={headerCellStyle}>: {scholarNo}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Session</Typography>
                        <Typography sx={headerCellStyle}>: {sessionName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Student Name</Typography>
                        <Typography sx={{ ...headerCellStyle, textTransform: "uppercase" }}>: {studentName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Class</Typography>
                        <Typography sx={headerCellStyle}>: {className}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Father's Name</Typography>
                        <Typography sx={headerCellStyle}>: {fatherName}</Typography>
                    </Box>
                </Box>

                {/* Particulars Table */}
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: "50px" }}>Sl.No</th>
                            <th style={thStyle}>Particulars</th>
                            <th style={{ ...thStyle, width: "120px", textAlign: "right" }}>Paid</th>
                        </tr>
                    </thead>
                    <tbody>
                        {receipt.particulars && receipt.particulars.length > 0 ? (
                            receipt.particulars.map((p, idx) => (
                                <tr key={idx}>
                                    <td style={tdStyle}>{idx + 1}</td>
                                    <td style={tdStyle}>{t(p.description)}</td>
                                    <td style={{ ...tdStyle, textAlign: "right" }}>{p.amount.toLocaleString()}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td style={tdStyle}>1</td>
                                <td style={tdStyle}>Total Fees</td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>{totalAmount.toLocaleString()}</td>
                            </tr>
                        )}
                        {/* Empty rows to fill space style */}
                        {[...Array(Math.max(0, 5 - (receipt.particulars?.length || 1)))].map((_, i) => (
                            <tr key={`empty-${i}`}>
                                <td style={{ ...tdStyle, height: "30px" }}></td>
                                <td style={tdStyle}></td>
                                <td style={tdStyle}></td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Total Row */}
                <Box sx={{ bgcolor: "#e0e0e0", display: "flex", justifyContent: "space-between", px: 2, py: 0.5, border: "1px solid #000", borderTop: "none" }}>
                    <Typography variant="body2" fontWeight="bold">Total</Typography>
                    <Typography variant="body2" fontWeight="bold">{totalAmount.toLocaleString()}</Typography>
                </Box>

                {/* Total in Words Row */}
                <Box sx={{ border: "1px solid #000", borderTop: "none", px: 2, py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        Total in Words : {numberToWords(totalAmount)}
                    </Typography>
                </Box>

                {/* Due Amount Row */}
                {dueAmount != null && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 0.5, border: "1px solid #000", borderTop: "none" }}>
                        <Typography variant="body2" fontWeight="bold">Due Amount</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            ₹{dueAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                )}

                {/* Pay Mode Section */}
                <Box sx={{ border: "1px solid #000", borderTop: "none" }}>
                    <Box sx={{ bgcolor: "#e0e0e0", textAlign: "center", py: 0.2, borderBottom: "1px solid #000" }}>
                        <Typography variant="body2" fontWeight="bold">PAY MODE INFORMATION</Typography>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", p: 1 }}>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontSize: "0.85rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Pay Mode</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.85rem" }}>: {payMode}</Typography>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontSize: "0.85rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Date & Time</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.85rem" }}>: {dateTime}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", gridColumn: "1 / -1" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontSize: "0.85rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Transaction No</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.85rem" }}>: {receipt.feeTransaction?.transactionId || receipt.paymentId || "-"}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <style>
                {`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    body {
                        background-color: white !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                }
                `}
            </style>
        </Box>
    );
};

export default FeeReceiptTemplate;
